import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Pagination from "../../components/common/Pagination";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchMembers } from "../../store/slices/membersSlice";
import { ApprovalStatus, UserRole } from "../../types/admin";
import {
  approvalStatusTone,
  formatApprovalStatus,
  formatDate,
  memberApprovalFilterOptions,
  roleLabelMap,
} from "../../utils/admin";

const MembersListPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { list, totalCount, page, pageSize, loadingList, listError } = useAppSelector((state) => state.members);

  const [role, setRole] = useState<UserRole | "">("");
  const [isActive, setIsActive] = useState<string>("");
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | "">("");
  const [query, setQuery] = useState("");
  const [pageState, setPageState] = useState(1);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    dispatch(
      fetchMembers({
        role: role || undefined,
        is_active: isActive === "true" ? true : isActive === "false" ? false : undefined,
        approval_status: approvalStatus || undefined,
        q: query || undefined,
        page: pageState,
        page_size: 20,
      })
    );
  }, [approvalStatus, dispatch, isActive, pageState, query, role]);

  const onSubmitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  return (
    <section className="admin-page">
      <h2 className="section-title">회원 관리</h2>
      <header className="list-toolbar">
        <form className="toolbar-left" onSubmit={onSubmitSearch}>
          <select
            className="chip-select"
            value={role}
            onChange={(event) => {
              setRole(event.target.value as UserRole | "");
              setPageState(1);
            }}
          >
            <option value="">전체 역할</option>
            <option value="owner">사업주</option>
            <option value="manager">점장</option>
            <option value="worker">직원</option>
            <option value="admin">관리자</option>
          </select>
          <select
            className="chip-select"
            value={isActive}
            onChange={(event) => {
              setIsActive(event.target.value);
              setPageState(1);
            }}
          >
            <option value="">전체 활성상태</option>
            <option value="true">활성</option>
            <option value="false">비활성(정지)</option>
          </select>
          <select
            className="chip-select"
            value={approvalStatus}
            onChange={(event) => {
              setApprovalStatus(event.target.value as ApprovalStatus | "");
              setPageState(1);
            }}
          >
            {memberApprovalFilterOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="search-box">
            <input
              placeholder="이름, 이메일, 연락처 검색"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button className="inline-search-btn" type="submit">
              검색
            </button>
          </div>
        </form>
        <p className="summary-text">
          총 <strong>{totalCount.toLocaleString()}</strong>명
          <span className="summary-text__hint"> · 사업주: 심사 대기 / 허용 / 반려</span>
        </p>
      </header>

      <div className="table-shell table-shell--scroll">
        <table className="admin-table admin-table--members">
          <thead>
            <tr>
              <th>회원 ID</th>
              <th>이름</th>
              <th>이메일</th>
              <th>연락처</th>
              <th>역할</th>
              <th>승인·가입 상태</th>
              <th>가입일</th>
              <th>상세</th>
            </tr>
          </thead>
          <tbody>
            {list.map((member) => {
              const tone = approvalStatusTone(member.approval_status);
              return (
                <tr key={member.user_id}>
                  <td className="text-mono">{member.user_id}</td>
                  <td>{member.user_name}</td>
                  <td>{member.email}</td>
                  <td>{member.phone_number}</td>
                  <td>{roleLabelMap[member.role]}</td>
                  <td>
                    <span className={`member-approval-badge member-approval-badge--${tone}`}>
                      {formatApprovalStatus(member.approval_status)}
                    </span>
                  </td>
                  <td>{formatDate(member.created_at)}</td>
                  <td>
                    <button
                      className="detail-icon-btn"
                      type="button"
                      onClick={() => navigate(`/admin/members/${member.user_id}`)}
                    >
                      상세
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={8}>
                  조회된 회원이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">회원 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}

      <Pagination page={page || pageState} pageSize={pageSize || 20} totalCount={totalCount} onPageChange={setPageState} />
    </section>
  );
};

export default MembersListPage;
